import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Package, ChefHat, CheckCircle2, Truck, Search } from 'lucide-react';
import { ordersClient, type Order } from '@happyspace/sdk/restaurant';

const STEPS = [
  { key: 'received', label: 'Received', icon: Package },
  { key: 'preparing', label: 'Preparing', icon: ChefHat },
  { key: 'ready', label: 'Ready', icon: CheckCircle2 },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
];

export default function OrderStatus() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get('order') || '');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!orderId || !email) return;
    setLoading(true);
    setError(null);
    try {
      const res = await ordersClient.getOrderStatus({ orderId, email });
      setOrder(res.order);
    } catch (err) {
      setError('We couldn\'t find that order. Double-check the order number and email.');
    } finally {
      setLoading(false);
    }
  }, [orderId, email]);

  useEffect(() => {
    if (order) {
      const interval = setInterval(fetchStatus, 30000);
      return () => clearInterval(interval);
    }
  }, [order, fetchStatus]);

  const activeIndex = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <>
      <Helmet>
        <title>Track Your Order — Biryani2Go</title>
      </Helmet>

      <section className="bg-surface min-h-[70vh] py-20">
        <div className="max-w-screen-md mx-auto px-5 md:px-8">
          <h1 className="font-display font-semibold text-3xl md:text-4xl text-maroon mb-2 text-center">Track Your Order</h1>
          <p className="text-ink-2 text-center mb-10">Enter your order number and email to see the status.</p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              fetchStatus();
            }}
            className="card-tin p-6 md:p-8 flex flex-col md:flex-row gap-3 mb-10"
          >
            <input
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Order number"
              className="field"
              required
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email used at checkout"
              type="email"
              className="field"
              required
            />
            <button type="submit" disabled={loading} className="btn-primary shrink-0 disabled:opacity-60">
              <Search size={17} /> {loading ? 'Looking...' : 'Track'}
            </button>
          </form>

          {error && (
            <div className="text-center text-ember mb-6">{error}</div>
          )}

          {order && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-tin p-8">
              <div className="flex justify-between mb-8">
                {STEPS.map((step, i) => (
                  <div key={step.key} className="flex flex-col items-center flex-1 relative">
                    {i > 0 && (
                      <div
                        className={`absolute top-5 right-1/2 w-full h-0.5 -z-10 ${
                          i <= activeIndex ? 'bg-ember' : 'bg-line'
                        }`}
                      />
                    )}
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                        i <= activeIndex ? 'bg-ember text-white' : 'bg-surface2 text-ink-2'
                      }`}
                    >
                      <step.icon size={18} />
                    </div>
                    <span className={`text-xs font-medium text-center ${i <= activeIndex ? 'text-maroon' : 'text-ink-2'}`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
              <div className="border-t border-line pt-6 flex justify-between">
                <span className="text-ink-2">Order #{order.orderNumber}</span>
                <span className="font-semibold text-ember tabular-nums">${order.total?.toFixed(2)}</span>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}
